import { Card, CardContent } from "../../components/beui/SurfaceCard";
import { useEffect, useState } from "react";
import { Download, Loader2, ShieldOff, Trash2, Check, AlertCircle, CheckCircle2, X } from "lucide-react";
import { api, type AuthUser } from "../../lib/api";
import { useI18n } from "../../lib/i18n";
import { MobileSettingsAction } from "../../components/MobileSettingsControls";

export function SettingsDataRights({ user, isMobileViewport }: { user: AuthUser; isMobileViewport: boolean }) {
  const { t } = useI18n();
  // Increment 40: data rights
  const [delState, setDelState] = useState<{ scheduled: boolean; scheduledAt: string | null; graceDays: number; activeDisputeCount?: number } | null>(null);
  const [exportBusy, setExportBusy] = useState(false);
  const [exportMsg, setExportMsg] = useState<string | null>(null);
  const [delBusy, setDelBusy] = useState(false);

  useEffect(() => { if (user) api.dataStatus().then((r) => setDelState(r.deletion)).catch(() => {}); }, [user]);

  async function downloadExport() {
    setExportBusy(true); setExportMsg(null);
    try {
      const r = await api.createDataExport();
      await api.downloadDataExport(r.downloadPath, r.export.id);
      setExportMsg("Your data export has downloaded.");
    } catch (e: any) { setExportMsg(e?.message || "Could not build your export."); }
    finally { setExportBusy(false); }
  }
  const [delModal, setDelModal] = useState(false); // v3.9.67 (QA #8)
  const [delReason, setDelReason] = useState("");
  async function requestDeletion(reason?: string) {
    setDelBusy(true);
    try { const r = await api.scheduleAccountDeletion(reason); setDelState(previous => ({ ...previous, scheduled: true, scheduledAt: r.scheduledAt, graceDays: r.graceDays })); setDelModal(false); setDelReason(""); }
    catch (e: any) { alert(e?.message || "Could not schedule deletion."); } finally { setDelBusy(false); }
  }
  async function cancelDeletion() {
    setDelBusy(true);
    try { await api.cancelAccountDeletion(); const r = await api.dataStatus(); setDelState(r.deletion); }
    catch (e: any) { alert(e?.message || "Could not cancel."); } finally { setDelBusy(false); }
  }

  return (<>
      <Card size="compact" className="settings-panel mt-4" data-settings-panel="data" data-testid="data-rights"><CardContent>
        <h3 className="font-bold text-navy mb-1 flex items-center gap-2"><Download className="w-4 h-4" />{t("settings.yourData")}</h3>
        {isMobileViewport ? <>
          <MobileSettingsAction title={t("settings.downloadData")} description={t("settings.downloadDataDescription")} onClick={() => { void downloadExport(); }} busy={exportBusy} testId="export-btn" />
          {exportMsg && <p className="settings-detail-note" role="status">{exportMsg}</p>}
          {delState?.scheduled ? <>
            <p className="settings-detail-note text-brand-red" role="status">Deletion scheduled{delState.scheduledAt ? ` for ${new Date(delState.scheduledAt).toLocaleDateString()}` : ""}. Your account will be permanently erased then. You can still cancel until that date.</p>
            <MobileSettingsAction title={t("settings.cancelScheduledDeletion")} description={t("settings.keepAccount")} onClick={() => { void cancelDeletion(); }} busy={delBusy} testId="cancel-delete-btn" />
          </> : <MobileSettingsAction title={t("settings.deleteAccount")} description={t("settings.deletionGraceDescription").replace("{days}", String(delState?.graceDays ?? 7))} onClick={() => setDelModal(true)} busy={delBusy} testId="delete-btn" />}
        </> : <>
          <div className="flex items-center gap-3 flex-wrap">
            <button onClick={downloadExport} disabled={exportBusy} className="btn-outline disabled:opacity-60" data-testid="export-btn">
              {exportBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}{t("settings.downloadData")}
            </button>
            {exportMsg && <span className="text-sm text-mut">{exportMsg}</span>}
          </div>

          <div className="border-t border-line mt-4 pt-4">
            {delState?.scheduled ? (
              <div>
                <p className="text-sm text-brand-red font-semibold flex items-center gap-2"><ShieldOff className="w-4 h-4" />Deletion scheduled{delState.scheduledAt ? ` for ${new Date(delState.scheduledAt).toLocaleDateString()}` : ""}.</p>
                <p className="text-xs text-mut mt-1">Your account will be permanently erased then. You can still cancel until that date.</p>
                <button onClick={cancelDeletion} disabled={delBusy} className="btn-navy mt-3 disabled:opacity-60" data-testid="cancel-delete-btn">{delBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}Cancel deletion</button>
              </div>
            ) : (
              <div>
                <p className="text-sm text-mut mb-2">Deleting your account removes your listings and anonymizes your data after a {delState?.graceDays ?? 7}-day grace period.</p>
                <button onClick={() => setDelModal(true)} disabled={delBusy} className="btn-outline text-brand-red disabled:opacity-60" data-testid="delete-btn"><Trash2 className="w-4 h-4" />{t("settings.deleteAccount")}</button>
              </div>
            )}
          </div>
        </>}
      </CardContent></Card>

      {/* v3.9.67 (QA #8) — dedicated account-deletion modal (consequences + optional reason) */}
      {delModal && (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={() => setDelModal(false)}>
          <div className="card w-full max-w-md p-0 overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="px-4 py-3 border-b border-line flex items-center gap-2">
              <ShieldOff className="w-4 h-4 text-brand-red" />
              <h3 className="font-bold text-navy">Delete your account</h3>
              <button onClick={() => setDelModal(false)} aria-label="Close" className="btn-ghost h-8 w-8 p-0 ml-auto"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-4 space-y-3">
              <p className="text-sm text-navy/90">Here's what happens if you continue:</p>
              {!!delState?.activeDisputeCount && <p role="status" className="rounded-xl bg-navy-50 p-3 text-sm text-navy">You have an ongoing dispute. Consider resolving it with the admin before your account is erased.</p>}
              <ul className="text-sm text-mut space-y-1.5">
                <li className="flex gap-2"><AlertCircle className="w-4 h-4 text-brand-red shrink-0 mt-0.5" />Your account is scheduled for permanent deletion after a <b className="text-navy">7-day grace period</b>.</li>
                <li className="flex gap-2"><AlertCircle className="w-4 h-4 text-brand-red shrink-0 mt-0.5" />Your listings, résumés, and profile are removed and your data is anonymized.</li>
                <li className="flex gap-2"><AlertCircle className="w-4 h-4 text-brand-red shrink-0 mt-0.5" />Deletion does not settle disputes or release held payment. After erasure, you cannot message or accept payment terms; saved dispute records remain.</li>
                <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-brand-green shrink-0 mt-0.5" />You can <b className="text-navy">cancel any time</b> before the grace period ends, right here in Settings.</li>
              </ul>
              <div>
                <label className="label">Reason for leaving <span className="text-mut font-normal">(optional — helps us improve)</span></label>
                <textarea value={delReason} onChange={(e) => setDelReason(e.target.value)} maxLength={500} className="field h-20 py-2 text-sm" placeholder="Tell us why you're deleting your account…" />
              </div>
            </div>
            <div className="px-4 py-3 border-t border-line flex gap-2">
              <button onClick={() => requestDeletion(delReason.trim() || undefined)} disabled={delBusy} className="btn-red flex-1 disabled:opacity-60">{delBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}Schedule deletion</button>
              <button onClick={() => setDelModal(false)} className="btn-outline flex-1">Keep my account</button>
            </div>
          </div>
        </div>
      )}
  </>);
}
